import { getSpotFromSearch } from '@canvas/branch-deploy';
import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';
import {
  DOCUMENT_CLEANED_HEADER,
  DOCUMENT_SESSION_COUNT_HEADER,
  DOCUMENT_UNTRUSTED_HEADER,
  DOCUMENT_SESSION_LIMIT_HEADER,
} from '@canvas/constants/untrusted';
import { base64DecodeBinary } from '@canvas/encoding';
import { createErrorClass } from '@canvas/error';
import { type LargeStepsSignedUrl } from '@canvas/model/steps';
import { RecoverableNetworkError, UnrecoverableNetworkError } from '@canvas/retry';
import { type TokenManager } from '@canvas/token-manager';

import { type CollabStrategy } from '../collab-strategy';

import { fetchWithTimeout } from './fetch-with-timeout';
import { GetDocumentStatusNcsMetrics } from './http-bootstrap-metrics';
import { fetchLargeStep } from './large-document-steps';

export type RemoteStepsUpdates = {
  updates: string[];
  largeUpdates?: LargeStepsSignedUrl[];
};

export const RelayURLParseError = createErrorClass('RelayURLParseError');

export function parseRelayUrl(relayUrl: string): { documentId: string; nonce: string } {
  const url = new URL(relayUrl);
  const rawDocumentId = url.pathname.split('/')[2] as string | undefined;
  const documentId = rawDocumentId ? decodeURIComponent(rawDocumentId) : rawDocumentId;
  const nonce = url.pathname.split('/')[3] as string | undefined;

  if (!documentId) {
    throw new RelayURLParseError(`Cannot find documentId in relay url`);
  }

  if (!nonce) {
    throw new RelayURLParseError(`Cannot find nonce in relay url`);
  }

  return { documentId, nonce };
}

export const StepsAuthenticationError = createErrorClass('StepsAuthenticationError');

export async function fetchDocumentSteps(
  url: string,
  documentId: string,
  signal: AbortSignal,
  headers?: HeadersInit,
) {
  const hostnameForLogs = url.startsWith('/') ? '<unspecified>' : new URL(url).hostname;

  const response = await fetchWithTimeout(url, { signal, headers }, { timeout: 30000 });
  if (!response.ok) {
    if (response.status === 401) {
      throw new StepsAuthenticationError();
    } else if (response.status === 408) {
      throw new RecoverableNetworkError(
        `Timed out attempting to fetch document steps for ${documentId}`,
      );
    } else if (response.status === 410) {
      throw new RecoverableNetworkError(
        `Attempted fetching stale document steps for ${documentId}`,
      );
    } else if (response.status >= 500) {
      throw new RecoverableNetworkError(
        `Received a server error status code ${response.status} from ${hostnameForLogs}`,
      );
    } else {
      throw new UnrecoverableNetworkError(
        `Unrecoverable error status code ${response.status} from ${hostnameForLogs}`,
      );
    }
  }
  return response;
}

export type RefreshTokenFn = () => Promise<void>;
export type CleanDocumentFn = (untrusted: boolean, cleaned: boolean) => void;
export type SessionCountFn = (sessionCount: number | undefined) => void;
export type SessionLimitFn = (sessionLimit: number | undefined) => void;

/**
 * Fetch steps from canvas-server (used for NCS collab infrastructure).
 * Calls the GET ERS steps endpoint: GET /api/_internal/document/:documentId/steps/:headStep
 */
export async function getRemoteStepsFromCanvasServer(
  headStep: string,
  documentId: string,
  signal: AbortSignal,
  tokenManager: TokenManager,
  serverUrl: string,
  headers?: Record<string, string>,
) {
  const token = await tokenManager.tokenForDocumentIdOrThrow(documentId);

  return fetchDocumentSteps(
    `${serverUrl}/api/_internal/document/${encodeURIComponent(
      documentId,
    )}/steps/${headStep}`,
    documentId,
    signal,
    {
      'x-access-token': token.accessToken,
      ...headers,
    },
  );
}

export type GetRemoteStepsUpdateOptions = {
  headStep: string;
  documentId: string;
  attempt: number;
  signal: AbortSignal;
  tokenManager: TokenManager;
  collabStrategy: CollabStrategy;
  refreshTokenFn?: RefreshTokenFn;
  cleanDocument?: CleanDocumentFn;
  sessionCount?: SessionCountFn;
  sessionLimit?: SessionLimitFn;
  serverUrl?: string;
  headers?: Record<string, string>;
};

export async function getRemoteStepsUpdate({
  headStep,
  documentId,
  attempt,
  signal,
  tokenManager,
  collabStrategy,
  refreshTokenFn,
  cleanDocument,
  sessionCount,
  sessionLimit,
  serverUrl,
  headers,
}: GetRemoteStepsUpdateOptions): Promise<Uint8Array[]> {
  const useNcs = collabStrategy.type === 'ncs' && serverUrl !== undefined;

  let response: Response;
  try {
    response = useNcs
      ? await getRemoteStepsFromCanvasServer(
          headStep,
          documentId,
          signal,
          tokenManager,
          serverUrl,
          headers,
        )
      : await getRemoteSteps(
          headStep,
          documentId,
          attempt,
          signal,
          tokenManager,
          headers,
        );
  } catch (err) {
    if (!signal.aborted && err instanceof StepsAuthenticationError) {
      await refreshTokenFn?.();
    }

    throw err;
  }

  if (cleanDocument) {
    const untrusted = response.headers.get(DOCUMENT_UNTRUSTED_HEADER) === 'true';
    const cleaned = response.headers.get(DOCUMENT_CLEANED_HEADER) === 'true';
    cleanDocument(untrusted, cleaned);
  }

  if (sessionCount) {
    const sessionCountHeader = response.headers.get(DOCUMENT_SESSION_COUNT_HEADER);
    const sessionCountValue = sessionCountHeader
      ? parseInt(sessionCountHeader, 10)
      : undefined;
    sessionCount(sessionCountValue);
  }

  if (sessionLimit) {
    const sessionLimitHeader = response.headers.get(DOCUMENT_SESSION_LIMIT_HEADER);
    const sessionLimitValue = sessionLimitHeader
      ? parseInt(sessionLimitHeader, 10)
      : undefined;
    sessionLimit(sessionLimitValue);
  }

  const body: RemoteStepsUpdates = await response.json();

  const binaryUpdates = body.updates.map((base64Update) =>
    base64DecodeBinary(base64Update),
  );

  if (body.largeUpdates && body.largeUpdates.length > 0) {
    const largeBinaryUpdates = await Promise.all(
      body.largeUpdates.map((largeStepResp) =>
        fetchLargeStep({
          signedDownloadUrl: largeStepResp,
          signal,
          maxRetries: 2,
          timeout: 30_000,
        }),
      ),
    );
    return binaryUpdates.concat(largeBinaryUpdates);
  }

  return binaryUpdates;
}

export type DocumentStatus = {
  latestStep: string | null;
  timestamp: number;
};

/**
 * Fetch document status from canvas-server (used for NCS collab infrastructure).
 * Calls the GET document-status endpoint: GET /api/_internal/document/:documentId/document-status
 */
export async function getDocumentStatus(
  documentId: string,
  signal: AbortSignal,
  tokenManager?: TokenManager,
  serverUrl?: string,
): Promise<DocumentStatus | null> {
  if (serverUrl === undefined) {
    return null;
  }

  if (!tokenManager) {
    return null;
  }

  const metrics = new GetDocumentStatusNcsMetrics();

  try {
    const token = await tokenManager.tokenForDocumentIdOrThrow(documentId);

    const response = await fetchDocumentSteps(
      `${serverUrl}/api/_internal/document/${encodeURIComponent(
        documentId,
      )}/document-status`,
      documentId,
      signal,
      {
        'x-access-token': token.accessToken,
      },
    );

    const body: DocumentStatus = await response.json();

    metrics.success();

    return body;
  } catch (err) {
    metrics.failure(err);

    throw err;
  }
}

export const getCanvasRelayBaseUrl = (attempt: number) => {
  const relaySpot =
    typeof globalThis.location !== 'undefined'
      ? getSpotFromSearch(globalThis.location.search, 'canvas-relay')
      : undefined;

  if (
    isFeatureFlagEnabled('useConnectionFallback') &&
    attempt > 2 &&
    typeof globalThis.location !== 'undefined'
  ) {
    const parent = new URL(globalThis.location.href);
    const base = `${parent.protocol}//${parent.host}`;
    return `${base}/canvas-collab/backup`;
  }

  const relayBaseUrl =
    typeof relaySpot === 'number'
      ? `https://canvas-relay-canvas-relay-${relaySpot}.atlassian-workers-test.workers.dev`
      : process.env.VITE_CANVAS_RELAY_URL;

  if (relayBaseUrl === '') {
    return '';
  } else {
    return relayBaseUrl;
  }
};

/**
 * @summary Get remote steps API - fetch with AuthZ powered by a token
 * crafted by a host product (embedded, EAP version of the application).
 */
export async function getRemoteSteps(
  headStep: string,
  documentId: string,
  attempt: number,
  signal: AbortSignal,
  tokenManager: TokenManager,
  headers?: Record<string, string>,
) {
  const token = await tokenManager.tokenForDocumentIdOrThrow(documentId);

  const relayBaseUrl = getCanvasRelayBaseUrl(attempt);

  return fetchDocumentSteps(
    `${relayBaseUrl}/api/public/document/${encodeURIComponent(
      documentId,
    )}/steps/${headStep}`,
    documentId,
    signal,
    {
      'x-access-token': token.accessToken,
      ...headers,
    },
  );
}
